import productCatalog from '../data/productCatalog.json';
import venueTemplates from '../data/venueTemplates.json';

/**
 * Core recommendation engine for PA System planning
 * Implements rule-based logic for system recommendations
 */

class RecommendationEngine {
  constructor() {
    this.products = productCatalog;
    this.templates = venueTemplates.templates;
  }

  /**
   * Main recommendation method
   * @param {Object} params - User input parameters
   * @returns {Array} - Array of recommended packages with ratings
   */
  getRecommendations(params) {
    const {
      venueType,
      length,
      width,
      height,
      capacity,
      usage,
      budget
    } = params;

    // Calculate area and volume
    const area = length * width;
    const volume = area * height;

    // Find matching templates
    const matchingTemplates = this.findMatchingTemplates({
      venueType,
      area,
      capacity,
      height,
      usage,
      budget
    });

    // Rank templates
    const rankedTemplates = this.rankTemplates(matchingTemplates, {
      area,
      capacity,
      usage,
      budget
    });

    // Build detailed recommendations
    const recommendations = rankedTemplates.slice(0, 3).map((template, index) => {
      return this.buildRecommendation(template, params, index + 1);
    });

    return recommendations;
  }

  /**
   * Find templates that match user criteria
   */
  findMatchingTemplates(criteria) {
    const { venueType, area, capacity, height, usage, budget } = criteria;

    return this.templates.filter(template => {
      // Match venue type
      if (template.venueType !== venueType) return false;

      // Match area range
      if (area < template.areaRange.min || area > template.areaRange.max) {
        // Allow 20% tolerance
        const tolerance = 0.2;
        if (area < template.areaRange.min * (1 - tolerance) || 
            area > template.areaRange.max * (1 + tolerance)) {
          return false;
        }
      }

      // Match capacity range
      if (capacity < template.capacityRange.min || capacity > template.capacityRange.max) {
        const tolerance = 0.2;
        if (capacity < template.capacityRange.min * (1 - tolerance) || 
            capacity > template.capacityRange.max * (1 + tolerance)) {
          return false;
        }
      }

      // Match usage (at least one usage should match)
      const usageMatch = template.usagePrimary.some(u => usage.includes(u));
      if (!usageMatch) return false;

      return true;
    });
  }

  /**
   * Rank matching templates based on fit score
   */
  rankTemplates(templates, criteria) {
    const { area, capacity, usage, budget } = criteria;

    return templates.map(template => {
      let score = 0;

      // Area fit score (0-40 points)
      const areaMid = (template.areaRange.min + template.areaRange.max) / 2;
      const areaDeviation = Math.abs(area - areaMid) / areaMid;
      score += Math.max(0, 40 - (areaDeviation * 40));

      // Capacity fit score (0-30 points)
      const capacityMid = (template.capacityRange.min + template.capacityRange.max) / 2;
      const capacityDeviation = Math.abs(capacity - capacityMid) / capacityMid;
      score += Math.max(0, 30 - (capacityDeviation * 30));

      // Usage match score (0-20 points)
      const usageMatches = usage.filter(u => template.usagePrimary.includes(u)).length;
      score += (usageMatches / usage.length) * 20;

      // Budget match score (0-10 points)
      const budgetMap = { economy: 0, 'mid-range': 1, premium: 2 };
      if (template.budgetCategory === budget) {
        score += 10;
      } else {
        const budgetDiff = Math.abs(budgetMap[budget] - budgetMap[template.budgetCategory]);
        score += Math.max(0, 10 - (budgetDiff * 5));
      }

      return { ...template, fitScore: Math.round(score) };
    }).sort((a, b) => b.fitScore - a.fitScore);
  }

  /**
   * Build detailed recommendation package
   */
  buildRecommendation(template, userParams, rank) {
    // Get product details
    const speakers = this.getProductDetails(template.system.speakers, 'speakers');
    const mixer = this.getProductDetails([template.system.mixer], 'mixers')[0];
    const amplifiers = this.getProductDetails(template.system.amplifiers, 'amplifiers');
    const accessories = this.getAccessoryDetails(template.system.accessories);

    // Calculate total price
    const totalPrice = this.calculateTotalPrice({
      speakers,
      mixer,
      amplifiers,
      accessories
    });

    // Calculate acoustic parameters
    const acousticParams = this.calculateAcousticParameters(
      userParams,
      { speakers, amplifiers }
    );

    // Generate rating (3 stars for best, 2 for second, 1 for third)
    const rating = 4 - rank;

    return {
      id: template.id,
      name: template.name,
      rank,
      rating,
      fitScore: template.fitScore,
      budgetCategory: template.budgetCategory,
      equipment: {
        speakers,
        amplifiers,
        mixer,
        accessories
      },
      pricing: {
        subtotal: totalPrice,
        installation: this.calculateInstallationCost(totalPrice),
        gst: Math.round(totalPrice * 0.18),
        total: Math.round(totalPrice * 1.18 + this.calculateInstallationCost(totalPrice))
      },
      technicalSpecs: {
        ...template.technicalSpecs,
        ...acousticParams
      },
      whyRecommended: template.whyRecommended,
      estimatedRange: template.estimatedPrice
    };
  }

  /**
   * Get product details from catalog
   */
  getProductDetails(items, category) {
    if (!items || items.length === 0) return [];

    return items.map(item => {
      const product = this.products[category].find(p => p.id === item.productId);
      if (!product) return null;

      return {
        ...product,
        quantity: item.quantity,
        placement: item.placement,
        subtotal: product.price * item.quantity
      };
    }).filter(Boolean);
  }

  /**
   * Get accessory details
   */
  getAccessoryDetails(items) {
    if (!items || items.length === 0) return [];

    return items.map(item => {
      const product = this.products.accessories.find(p => p.id === item.productId);
      if (!product) return null;

      return {
        ...product,
        quantity: item.quantity,
        description: item.description,
        subtotal: product.price * item.quantity
      };
    }).filter(Boolean);
  }

  /**
   * Calculate total equipment price
   */
  calculateTotalPrice(equipment) {
    let total = 0;

    ['speakers', 'amplifiers', 'accessories'].forEach(category => {
      if (equipment[category]) {
        equipment[category].forEach(item => {
          total += item.subtotal;
        });
      }
    });

    if (equipment.mixer) {
      total += equipment.mixer.price;
    }

    return total;
  }

  /**
   * Calculate installation cost (10-15% of equipment cost)
   */
  calculateInstallationCost(equipmentCost) {
    const percentage = equipmentCost > 500000 ? 0.10 : 0.15;
    return Math.round(equipmentCost * percentage);
  }

  /**
   * Calculate acoustic parameters
   */
  calculateAcousticParameters(venue, system) {
    const area = venue.length * venue.width;
    const volume = area * venue.height;

    // Calculate total speaker coverage
    let totalCoverage = 0;
    system.speakers.forEach(speaker => {
      totalCoverage += speaker.coverageArea * speaker.quantity;
    });

    // Calculate coverage efficiency
    const coverageEfficiency = Math.min(100, (totalCoverage / area) * 100);

    // Estimate SPL at listening position
    const estimatedSPL = this.estimateSPL(system.speakers, area, venue.height);

    return {
      calculatedArea: `${area} sq.ft`,
      calculatedVolume: `${volume} cu.ft`,
      coverageEfficiency: `${Math.round(coverageEfficiency)}%`,
      estimatedSPL: `${estimatedSPL} dB`,
      powerPerSqFt: `${Math.round((this.getTotalPower(system) / area) * 10) / 10} W/sq.ft`
    };
  }

  /**
   * Estimate SPL at listening position
   */
  estimateSPL(speakers, area, height) {
    if (speakers.length === 0) return 90;

    // Get average speaker sensitivity
    const avgSensitivity = speakers.reduce((sum, s) => sum + s.sensitivity, 0) / speakers.length;
    
    // Distance loss calculation (simplified)
    const avgDistance = Math.sqrt(area / speakers[0].quantity) / 2;
    const distanceLoss = 20 * Math.log10(avgDistance / 3.28); // Convert to meters

    // Estimated SPL
    const estimatedSPL = avgSensitivity - distanceLoss - 6; // 6dB room loss

    return Math.round(estimatedSPL);
  }

  /**
   * Get total system power
   */
  getTotalPower(system) {
    let totalPower = 0;

    system.speakers.forEach(speaker => {
      totalPower += speaker.powerRating * speaker.quantity;
    });

    return totalPower;
  }
}

// Export singleton instance
const recommendationEngine = new RecommendationEngine();
export default recommendationEngine;
